import React from 'react';
import { MapPin } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { NumericInput } from '../../../../common/inputs';
import { ConsolidationSourceLine } from '../../../../../types/consolidation';
import { SourceSelectionState } from '../types';
import { clampQty } from '../utils';

interface SourceListProps {
    sources: ConsolidationSourceLine[];
    selectionFor: (fromBinCode: string) => SourceSelectionState;
    onToggle: (fromBinCode: string) => void;
    onQtyChange: (fromBinCode: string, qty: string) => void;
    disabled?: boolean;
}

/**
 * Kildeliste for «Trekk inn hit» og «Samle vare»: én rad per kilde-lokasjon
 * med checkbox (forhåndsavhuket), saldo-info og justerbart antall
 * (clamp 0..foreslått kvantum).
 */
export const SourceList: React.FC<SourceListProps> = ({
    sources,
    selectionFor,
    onToggle,
    onQtyChange,
    disabled = false
}) => {
    const { t } = useTranslation();

    return (
        <div className="divide-y divide-gray-100">
            {sources.map((source) => {
                const selection = selectionFor(source.fromBinCode);

                return (
                    <div
                        key={source.fromBinCode}
                        className={`flex items-center gap-3 py-2 min-h-[44px] ${selection.checked ? '' : 'opacity-50'}`}
                    >
                        <label className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer min-h-[44px]">
                            <input
                                type="checkbox"
                                checked={selection.checked}
                                onChange={() => onToggle(source.fromBinCode)}
                                disabled={disabled}
                                className="h-5 w-5 text-cyan-500 border-gray-300 rounded focus:ring-cyan-500 flex-shrink-0"
                            />
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                    <span className="font-mono text-sm font-medium text-gray-900 break-all">
                                        {source.fromBinCode}
                                    </span>
                                    {source.freesBin && (
                                        <span className="px-1.5 py-0.5 bg-green-100 text-green-800 text-xs rounded-full whitespace-nowrap">
                                            {t('warehouse.modules.consolidation.pullIn.freesBin', {}, 'frigjør lokasjon')}
                                        </span>
                                    )}
                                    {source.capacityLimited && (
                                        <span className="px-1.5 py-0.5 bg-orange-100 text-orange-800 text-xs rounded-full whitespace-nowrap">
                                            {t('warehouse.modules.consolidation.capacity.limited', {}, 'Compass: ikke plass')}
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {t('warehouse.modules.consolidation.pullIn.balance', { '0': source.binBalance }, `Saldo: ${source.binBalance}`)}
                                    {source.reservedQuantity > 0 && (
                                        <span className="text-orange-600 ml-2">
                                            {t('warehouse.modules.consolidation.pullIn.reserved', { '0': source.reservedQuantity }, `Reservert: ${source.reservedQuantity}`)}
                                        </span>
                                    )}
                                </div>
                                {source.warnings.length > 0 && (
                                    <div className="text-xs text-orange-600 break-words">
                                        {source.warnings.join(' · ')}
                                    </div>
                                )}
                            </div>
                        </label>
                        <div className="flex items-center gap-1 flex-shrink-0">
                            <NumericInput
                                mode="decimal"
                                quantity
                                value={selection.qty}
                                onChange={(e) => onQtyChange(source.fromBinCode, clampQty(e.target.value, source.quantity))}
                                disabled={disabled || !selection.checked}
                                className="w-20 p-2 border border-gray-300 rounded-lg text-right text-base focus:ring-2 focus:ring-cyan-500 focus:border-transparent min-h-[44px]"
                            />
                            <span className="text-xs text-gray-400 whitespace-nowrap">/ {source.quantity}</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
